import { useModalHooks } from "App/State/Hooks/useModalHooks";
import { useNotification } from "App/State/Hooks/useNotificationHooks";
import { IEditFeature, IFeature } from "App/Typings/AbacFeature";
import { IFeatureAttributeDto } from "App/Typings/AbacFeatureAttribute";
import { ITransferListItem, ITransferListPanel } from "App/Typings/TransferList";
import { useFormik } from "formik";
import {
  useEditAbacFeatureMutation,
  useGetAllAttributesForAbacAppQuery,
  useGetAllFeatureAttributesForAbacFeatureQuery,
  useGetFeatureVariantsQuery,
} from "Pages/ABAC/AbacAppQueries";
import { useCallback, useEffect, useState } from "react";
import { AnySchema, array, bool, object, string } from "yup";

export type FeatureDetailsState = ReturnType<typeof useFeatureDetailsState>;

export default function useFeatureDetailsState(currentFeature: IFeature) {
  const { data: featureVariants } = useGetFeatureVariantsQuery(currentFeature.id);
  const editFeatureSuccessMessage = "Successfully updated feature information!";
  const editFeatureErrorMessage = "Error updating feature information";
  const { displaySuccess, displayError } = useNotification();
  const { openModal } = useModalHooks();

  const { mutate: editFeature, isLoading: editIsLoading } = useEditAbacFeatureMutation(
    currentFeature.id,
    currentFeature.app.id
  );

  const { data: appAttributes, isLoading: appAttributesAreLoading } = useGetAllAttributesForAbacAppQuery(
    currentFeature.app.id
  );

  const { data: featureAttributes, isLoading: featureAttributesAreLoading } =
    useGetAllFeatureAttributesForAbacFeatureQuery(currentFeature.id);

  const [isActive, setIsActive] = useState(currentFeature.isActive);

  const [assignedAttributes, setAssignedAttributes] = useState<ITransferListItem[]>([]);
  const [unassignedAttributes, setUnassignedAttributes] = useState<ITransferListItem[]>([]);

  const rightTransferPanel: ITransferListPanel = {
    title: "Unassigned Attributes",
    listItems: unassignedAttributes,
    setListItems: setUnassignedAttributes,
    listIsLoading: appAttributesAreLoading,
    listHasAddItem: false,
  };

  const leftTransferPanel: ITransferListPanel = {
    title: "Assigned Attributes",
    listItems: assignedAttributes,
    setListItems: setAssignedAttributes,
    listIsLoading: featureAttributesAreLoading,
    listHasAddItem: true,
    addItemLabel: "Create New Attribute",
    addItemFunction: () => openModal(),
  };

  //Validation for the edit feature form
  const validationSchema = object({
    featureName: string()
      .required("Feature Name is required")
      .trim()
      .max(100, "Feature name must be less than 100 characters"),
    featureDescription: string().trim().max(500, "Feature description must be less than 500 characters"),
    requireAll: bool(),
    hasVariations: bool(),
    variationAttributes: array<AnySchema, IFeatureAttributeDto>().when("hasVariations", {
      is: true,
      then: (schema) =>
        schema
          .test(
            "one-or-more-variations",
            "At least one variation is required",
            (variations) => variations && variations.length > 0
          )
          .test(
            "values-in-range",
            "Variation distribution values must be numbers between 0 and 100",
            (variations) =>
              variations?.every((variation) => variation.distribution >= 0 && variation.distribution <= 100)
          )
          .test(
            "100-percent-total",
            "Sum of distribution values must be 100%",
            (variations) => variations?.reduce((acc: number, attr) => acc + attr.distribution, 0) === 100
          )
          .test("no-empty-names", "Variation names cannot be empty", (variations) =>
            variations?.every((variation) => variation.attributeName && variation.attributeName.trim() !== "")
          )
          .test("unique-names", "Variation names must be unique", (variations) => {
            const names =
              variations
                ?.filter((variation) => variation.attributeName)
                .map((variation) => variation.attributeName.toLowerCase().trim()) ?? [];
            return names.length === 0 || names.length === new Set(names).size;
          }),
      otherwise: (schema) => schema.optional(),
    }),
  });

  //Initial edit feature formik setup
  const formik = useFormik({
    initialValues: {
      featureName: currentFeature.name,
      featureDescription: currentFeature.description,
      requireAll: currentFeature.requireAll,
      hasVariations: currentFeature.hasVariations,
      variationAttributes: featureVariants?.defaultFeatureAttributes ?? [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // Create edit feature object
      let editedFeature: IEditFeature = {
        id: currentFeature.id,
        name: values.featureName.trim(),
        description: values.featureDescription.trim(),
        requireAll: values.requireAll,
        hasVariations: values.hasVariations,
        isActive: isActive,
        assignedAttributeIds: assignedAttributes.map((aa) => aa.item.id),
        unassignedAttributeIds: unassignedAttributes.map((ua) => ua.item.id),
        variationAttributes: values.hasVariations
          ? values.variationAttributes.map((va) => ({
              ...va,
              id: va.id.startsWith("new-") ? "00000000-0000-0000-0000-000000000000" : va.id,
              attributeName: va.attributeName.trim(),
            }))
          : [],
      };
      submitEditFeature(editedFeature);
    },
  });

  //Create a new Feature
  const submitEditFeature = useCallback(
    function editFeatureInformation(editedFeature: IEditFeature) {
      if (!editIsLoading) {
        editFeature(editedFeature, {
          onError: () => {
            displayError(editFeatureErrorMessage);
          },
          onSuccess: () => {
            displaySuccess(editFeatureSuccessMessage);
          },
        });
      }
    },
    [editFeature, editIsLoading, displayError, displaySuccess]
  );

  // Populate the TransferList item lists
  useEffect(() => {
    let leftList: ITransferListItem[] = [];
    let rightList: ITransferListItem[] = [];

    if (!appAttributesAreLoading) {
      rightList = appAttributes!.map((attribute) => ({
        item: attribute,
        label: attribute.name,
      }));
    }

    if (!featureAttributesAreLoading) {
      leftList = featureAttributes!.map((featureAttribute) => ({
        item: featureAttribute.attribute,
        label: featureAttribute.attribute.name,
      }));
    }

    rightList = rightList.filter((i) => !leftList.find((f) => f.item.id === i.item.id));

    setUnassignedAttributes(rightList);
    setAssignedAttributes(leftList);
  }, [
    appAttributes,
    featureAttributes,
    featureAttributesAreLoading,
    appAttributesAreLoading,
    setAssignedAttributes,
    setUnassignedAttributes,
  ]);

  return {
    getters: {
      isActive,
      formik,
      currentFeature,
      rightTransferPanel,
      leftTransferPanel,
    },
    setters: {
      setIsActive,
    },
  };
}
